import classNames from "classnames";
import React from "react";

import { Switch } from "@/components/switch";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { Day } from "@/offering/day_utils";
import {
  MIN_RECURRING_COUNT,
  RECURRENCE_INTERVALS,
  RecurrenceRule,
} from "@/offering/recurrence";
import { RecurringExpansion } from "@/offering_v2/utils/expand_recurrence";
import {
  bookingsCount,
  clampNote,
  missingWindowHint,
  presetLabel,
  repeatHint,
} from "./recurring_booking_copy";
import { RecurringDatesList } from "./recurring_dates_list";
import styles from "./recurring_repeat_pane.module.css";

interface RepeatToggleRowProps {
  on: boolean;
  onToggle: () => void;
  anchor?: Day;
  // What the switch repeats: a time window (hourly) or a day (daily). While
  // disabled the hint says which of the two is missing.
  durationUnit: "hour" | "day";
  disabled: boolean;
}

// The "Repeat this booking" switch. Rendered only on rooms the booking policy
// reports as eligible; disabled until there is a booking to repeat.
export function RepeatToggleRow({
  on,
  onToggle,
  anchor,
  durationUnit,
  disabled,
}: RepeatToggleRowProps) {
  const { fbt } = useI18n();
  // While Repeat is on without a window the pane already says what is
  // missing, so the row does not repeat it.
  const hint = disabled
    ? missingWindowHint(durationUnit, fbt)
    : repeatHint(anchor, fbt);
  return (
    <div className={styles.toggleRow} data-testid="repeat-toggle-row">
      <div className="min-w-0">
        <div style={{ fontSize: 16 }}>{fbt("Repeat this booking")}</div>
        <div className={styles.hint} data-testid="repeat-toggle-hint">
          {hint}
        </div>
      </div>
      <Switch
        testID="repeat-toggle"
        ariaLabel={fbt("Repeat this booking")}
        value={on}
        disabled={disabled}
        onChange={onToggle}
      />
    </div>
  );
}

export type RecurringPaneStatus =
  | "no_window"
  | "loading"
  | "error"
  | "anchor_unavailable"
  | "too_few"
  | "ready";

export function paneStatus(args: {
  hasWindow: boolean;
  loading: boolean;
  error: boolean;
  expansion?: RecurringExpansion;
}): RecurringPaneStatus {
  if (!args.hasWindow) return "no_window";
  if (args.error) return "error";
  if (args.loading || !args.expansion) return "loading";
  if (args.expansion.anchorUnavailable) return "anchor_unavailable";
  if (args.expansion.maxCount < MIN_RECURRING_COUNT) return "too_few";
  return "ready";
}

interface RecurringRepeatPaneProps {
  rule: RecurrenceRule;
  onChangeRule: (rule: RecurrenceRule) => void;
  expansion?: RecurringExpansion;
  status: RecurringPaneStatus;
  // Desktop shows the dates beside the controls; the mobile sheet shows a
  // button that opens them as their own screen.
  variant: "pane" | "inline";
  // What is still missing when there is no window yet: a daily picker has no
  // time to choose.
  durationUnit: "hour" | "day";
  onReview?: () => void;
}

export function RecurringRepeatPane({
  rule,
  onChangeRule,
  expansion,
  status,
  variant,
  durationUnit,
  onReview,
}: RecurringRepeatPaneProps) {
  const { fbt } = useI18n();

  const maxCount = expansion?.maxCount;
  const canIncrement = maxCount === undefined || rule.count < maxCount;
  const canDecrement = rule.count > MIN_RECURRING_COUNT;

  const statusHint = (() => {
    switch (status) {
      case "no_window":
        return missingWindowHint(durationUnit, fbt);
      case "loading":
        return fbt("Checking your dates…");
      case "error":
        return fbt("We couldn't check these dates. Try again.");
      case "anchor_unavailable":
        return fbt("This date isn't available at this time.");
      case "too_few":
        return fbt(
          "This date and time can't repeat inside the booking window."
        );
      default:
        return undefined;
    }
  })();

  const note =
    expansion && status === "ready" ? clampNote(expansion, fbt) : undefined;
  const showList = status === "ready" && !!expansion;

  return (
    <div className={styles.pane} data-testid="recurring-repeat-pane">
      <div className={styles.header}>
        {variant === "pane" && (
          <>
            <div className={styles.title}>{fbt("Your recurring booking")}</div>
            <div style={{ height: 14 }} />
          </>
        )}
        <div
          className={styles.chips}
          role="radiogroup"
          aria-label={fbt("Repeat")}
        >
          {RECURRENCE_INTERVALS.map((interval) => {
            const selected = interval === rule.interval;
            return (
              <button
                key={interval}
                type="button"
                role="radio"
                aria-checked={selected}
                className={classNames(
                  styles.chip,
                  selected && styles.chipSelected
                )}
                onClick={() => onChangeRule({ ...rule, interval })}
              >
                {presetLabel(interval, fbt)}
              </button>
            );
          })}
        </div>
        <div style={{ height: 16 }} />
        <div className={styles.stepperRow}>
          <span>{fbt("Ends after")}</span>
          <div className={styles.stepper}>
            <button
              type="button"
              aria-label={fbt("Fewer bookings")}
              className={styles.stepperButton}
              disabled={!canDecrement}
              onClick={() => onChangeRule({ ...rule, count: rule.count - 1 })}
            >
              −
            </button>
            <span className={styles.stepperValue} data-testid="repeat-count">
              {bookingsCount(rule.count, fbt)}
            </span>
            <button
              type="button"
              aria-label={fbt("More bookings")}
              className={styles.stepperButton}
              disabled={!canIncrement}
              onClick={() => onChangeRule({ ...rule, count: rule.count + 1 })}
            >
              +
            </button>
          </div>
        </div>
        {note && (
          <div className={styles.note} data-testid="repeat-clamp-note">
            {note}
          </div>
        )}
        {statusHint && (
          <div
            className={classNames(styles.hint, "mt-3")}
            data-testid="repeat-status"
          >
            {statusHint}
          </div>
        )}
        {showList && (
          <div className={classNames(styles.hint, "mt-3")}>
            {fbt("Tinted dates are your recurring booking.")}
          </div>
        )}
        {variant === "inline" && onReview && status !== "no_window" && (
          // Rendered from the moment there is a window to check, disabled
          // until the horizon has answered, so the pane keeps its height and
          // the scroll that revealed it stays valid.
          <>
            <div style={{ height: 14 }} />
            <button
              type="button"
              className={styles.reviewButton}
              onClick={onReview}
              disabled={!showList}
              data-testid="repeat-review"
            >
              {showList
                ? fbt(
                    "Review all " +
                      fbt.param("count", expansion.booked.length) +
                      " bookings"
                  )
                : fbt("Review bookings")}
            </button>
          </>
        )}
      </div>
      {showList && variant === "pane" && (
        <div className={styles.list}>
          <RecurringDatesList expansion={expansion} />
        </div>
      )}
    </div>
  );
}
